import { type FC } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { Link, Rating } from 'base-ui'

import { useGlobalConfigContext } from '../../../global-config'
import { TENANT } from '../../../tenant'
import { TosSuperscriptHowProductRatingCalculated } from '../../../tos-superscripts'
import { productAccessorIsPrescriptionProduct } from '../../model/productAccessor/productAccessorIsPrescriptionProduct'
import { productAccessorIsProductNotInCatalog } from '../../model/productAccessor/productAccessorIsProductNotInCatalog'
import { productAccessorShouldDisplayReviews } from '../../model/productAccessor/productAccessorShouldDisplayReviews'
import { productReviewAccessorGetAllSummary } from '../../model/productReviewAccessor'
import { useProductPageContext } from '../productPageContext'

import { ProductHeadRatingAndQuestionsItemContainer } from './ProductHeadRatingAndQuestionsItemContainer'

const DELIMITER = '|'

export const ProductHeadRatingAndQuestions: FC = () => {
  const { tenant } = useGlobalConfigContext()
  const { faqs, product, reviewData } = useProductPageContext()
  const intl = useIntl()
  const shouldRenderNumberOfQuestions = faqs && faqs.length > 0
  const reviewsSummary = productReviewAccessorGetAllSummary(reviewData)
  const ratingValue = Math.round(reviewsSummary.averageRating)

  const shouldRenderReviews = productAccessorShouldDisplayReviews({
    product,
    tenant,
  })

  // In CH tenant we need to hide Reviews for RX products. The RX products can be purchased via Mediservice.
  const isPrescriptionProductInChTenant =
    productAccessorIsPrescriptionProduct(product) && tenant === TENANT.CH

  if (
    productAccessorIsProductNotInCatalog(product.stock_status) ||
    (!reviewsSummary.ratingCount && !shouldRenderNumberOfQuestions) ||
    !shouldRenderReviews
  ) {
    return null
  }

  return (
    <div className="flex flex-col items-start gap-1 tablet:items-end">
      {!isPrescriptionProductInChTenant &&
      (ratingValue || shouldRenderNumberOfQuestions) ? (
        <span className="flex">
          <Rating<'a'>
            aria-label={intl.formatMessage({ id: 'general.label.Ratings' })}
            className="text-2xl"
            href="#user-reviews-section"
            readOnly
            slots={{
              iconContainer: ProductHeadRatingAndQuestionsItemContainer,
              root: 'a',
            }}
            value={ratingValue}
          />
          <TosSuperscriptHowProductRatingCalculated className="top-1 ml-1 text-dark-primary-strong" />
        </span>
      ) : null}
      <div className="flex flex-row-reverse flex-wrap">
        {!isPrescriptionProductInChTenant && (
          <Link
            className="text-dark-primary-max"
            data-qa-id="number-of-ratings-text"
            href="#user-reviews-section"
          >
            <FormattedMessage
              id="general.label.product.numberOfRatings"
              values={{ numberOfRatings: reviewsSummary.ratingCount }}
            />
          </Link>
        )}

        {shouldRenderNumberOfQuestions && !isPrescriptionProductInChTenant ? (
          <span className="mx-2">{DELIMITER}</span>
        ) : null}

        {shouldRenderNumberOfQuestions ? (
          <Link
            className="text-dark-primary-max"
            data-qa-id="faq-link"
            href="#user-questions-section"
          >
            <FormattedMessage
              id="general.label.product.numberOfQuestions"
              values={{ numberOfQuestions: faqs.length }}
            />
          </Link>
        ) : null}
      </div>
    </div>
  )
}
